<template>
  <div
    class="d-flex align-center justify-space-between pa-2 rounded"
    :style="rowStyle"
    :data-testid="`aggregate-learning-leaderboard-row-${rank}`"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="onRowClick"
    @keydown.enter="onRowClick"
    @keydown.space.prevent="onRowClick"
  >
    <div class="d-flex align-center" :style="{ minWidth: 0 }">
      <Typography variant="subtitle-2" class="text-right mr-2" :style="{ minWidth: '20px' }">
        {{ rank }}
      </Typography>

      <img
        v-if="showAvatarImage"
        :src="avatarUrl"
        :alt="displayName"
        class="rounded-circle flex-shrink-0"
        :style="avatarStyle"
        @error="onAvatarImageError"
      />
      <div
        v-else
        class="d-flex align-center justify-center rounded-circle flex-shrink-0"
        :style="initialsAvatarStyle"
      >
        <Typography variant="caption-2" :style="{ color: 'white' }">
          {{ initials }}
        </Typography>
      </div>

      <div class="d-flex align-center ga-1 ml-2" :style="{ minWidth: 0, gap: '8px' }">
        <Icon v-if="isTopThree" icon="mdi-star" small color="black" />
        <Typography
          variant="subtitle-1"
          class="name-clamp"
          :style="{ minWidth: 0 }"
          :title="displayName"
        >
          {{ isCurrentUser ? $t("common.labels.you") : displayName }}
        </Typography>
      </div>
    </div>

    <div class="d-flex align-center">
      <div class="d-flex align-center" :style="learningStyle">
        <Icon icon="mdi-school" small :color="learningIconColor" class="mr-1" />
        <Typography variant="subtitle-2" :class="learningCountClass">
          {{ learningCompleted }}
        </Typography>
      </div>

      <div class="d-flex align-center" :style="badgesStyle">
        <Icon icon="mdi-medal" small :color="badgesIconColor" class="mr-1" />
        <Typography variant="subtitle-2" :class="badgesCountClass">
          {{ badgesEarned }}
        </Typography>
      </div>
    </div>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Icon from "@/components/generic/Icon.vue";

const RANK_HIGHLIGHTS = {
  1: { border: "#e8a200", from: "#fffce9", to: "#fffadc" },
  2: { border: "#91a3ba", from: "#f8fafc", to: "#eff7ff" },
  3: { border: "#c24c00", from: "#fff7ed", to: "#fff2e1" },
};

export default {
  name: "AggregateLearningLeaderboardRow",
  components: { Typography, Icon },
  props: {
    rank: {
      type: [Number, String],
      required: true,
    },
    userId: {
      type: String,
      default: null,
    },
    displayName: {
      type: String,
      required: true,
    },
    initials: {
      type: String,
      default: "",
    },
    avatarUrl: {
      type: String,
      default: "",
    },
    learningCompleted: {
      type: Number,
      default: 0,
    },
    badgesEarned: {
      type: Number,
      default: 0,
    },
    isCurrentUser: {
      type: Boolean,
      default: false,
    },
    activeSort: {
      type: String,
      default: "learning",
      validator: (v) => ["learning", "badges"].includes(v),
    },
    accentColor: {
      type: String,
      default: "#077DAF",
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      avatarImageFailed: false,
    };
  },
  watch: {
    avatarUrl() {
      // Reset the failure flag if a new URL is provided so we re-attempt loading.
      this.avatarImageFailed = false;
    },
  },
  methods: {
    onAvatarImageError() {
      this.avatarImageFailed = true;
    },
    onRowClick() {
      if (!this.clickable || !this.userId) return;
      this.$emit("click", this.userId);
    },
  },
  computed: {
    showAvatarImage() {
      return !!this.avatarUrl && !this.avatarImageFailed;
    },
    resolvedAvatarBgColor() {
      // Avatar background always uses the Vuetify theme's brandPrimary so initials circles
      // stay consistent across the app.
      return this.$vuetify?.theme?.themes?.light?.brandPrimary;
    },
    accentTint() {
      const hex = this.accentColor;
      if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
        return "#E6F5FD";
      }
      let h = hex.replace("#", "");
      if (h.length === 3)
        h = h
          .split("")
          .map((c) => c + c)
          .join("");
      if (h.length !== 6) return "#E6F5FD";
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      if ([r, g, b].some((v) => Number.isNaN(v))) return "#E6F5FD";
      return `rgba(${r}, ${g}, ${b}, 0.15)`;
    },
    numericRank() {
      const n = Number(this.rank);
      return Number.isFinite(n) ? n : null;
    },
    rankHighlight() {
      return RANK_HIGHLIGHTS[this.numericRank] || null;
    },
    isTopThree() {
      return !!this.rankHighlight;
    },
    rowStyle() {
      const base = this.clickable && this.userId ? { cursor: "pointer" } : {};
      if (this.rankHighlight) {
        return {
          ...base,
          background: `linear-gradient(to right, ${this.rankHighlight.from}, ${this.rankHighlight.to})`,
          borderLeft: `4px solid ${this.rankHighlight.border}`,
        };
      }
      if (this.isCurrentUser) {
        return {
          ...base,
          backgroundColor: this.accentTint,
          border: `1px solid ${this.accentColor}`,
        };
      }
      return base;
    },
    avatarStyle() {
      const style = { width: "40px", height: "40px", objectFit: "cover" };
      if (this.rankHighlight) {
        style.border = `2px solid ${this.rankHighlight.border}`;
      }
      return style;
    },
    initialsAvatarStyle() {
      const style = {
        backgroundColor: this.resolvedAvatarBgColor,
        width: "24px",
        height: "24px",
      };
      if (this.rankHighlight) {
        style.border = `2px solid ${this.rankHighlight.border}`;
      }
      return style;
    },
    isLearningActive() {
      return this.activeSort === "learning";
    },
    isBadgesActive() {
      return this.activeSort === "badges";
    },
    learningIconColor() {
      return this.isLearningActive ? this.accentColor : "mediumGrey";
    },
    badgesIconColor() {
      return this.isBadgesActive ? this.accentColor : "mediumGrey";
    },
    learningCountClass() {
      return this.isLearningActive ? "font-weight-bold" : "font-weight-regular";
    },
    badgesCountClass() {
      return this.isBadgesActive ? "font-weight-bold" : "font-weight-regular";
    },
    learningStyle() {
      return this.isLearningActive ? { minWidth: "50px" } : { opacity: 0.6, minWidth: "50px" };
    },
    badgesStyle() {
      return this.isBadgesActive ? { minWidth: "50px" } : { opacity: 0.6, minWidth: "50px" };
    },
  },
};
</script>

<style scoped>
.name-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  text-align: left;
}
</style>
